import axios from 'axios'
import { Block } from 'viem'

import { findMax, getMedian } from 'utils/helpers'

import { etherNumbers } from 'constants/variables'

import {
  FeeHistory,
  RpcCallParams,
  FeeHistoryResponse,
  CalculateFeesParams,
  MaxPriorityFeePerGasResponse,
} from './types'

//PERCENTILE 5 - значит, что у 5% комиссий из блока - priority fee был ниже отданного значения.
// Здесь можно также брать параметр gasUsedRatio и смотреть на сколько блок был заполнен в принципе. Возможно, он был заполнен всего на 10%.
const PERCENTILE = 5
//дефолтное значение priority fee, если оно не определено. Можно указать 0.3 gwei.
const MIN_PRIORITY = 500000000 // 0.5 gwei
const PERCENT_MULTIPLIER = 100
//число блоков (начиная с последнего), у которых мы берем исторические данные (по priority fee).
const DEFAULT_BLOCK_COUNT = 10
//процент(!) увеличения priority fee, выраженный в gwei. Если процент прироста в переводе больше чем 200 gwei - мы режем массив priority fee начиная с этой (самой высокой) точки цены.
const PRIORITY_FEE_INCREASE_BOUNDARY = 200000000000 // 200 gwei

export async function estimateFees(block: Block, rpcUrl: string) {
  try {
    if (!block.baseFeePerGas) {
      return {
        baseFee: null,
        maxFeePerGas: null,
        maxPriorityFeePerGas: null,
      }
    }

    const { data } = await axios.post<RpcCallParams, { data: FeeHistoryResponse }>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'eth_feeHistory',
      params: [DEFAULT_BLOCK_COUNT, 'latest', [PERCENTILE]],
      id: 1,
    })

    const fees = await calculateFees({ baseFee: block.baseFeePerGas, feeHistory: data.result, rpcUrl })

    return fees
  } catch (err) {
    return {
      baseFee: null,
      maxFeePerGas: null,
      maxPriorityFeePerGas: null,
    }
  }
}

function calculatePriorityFeeEstimate(feeHistory?: FeeHistory) {
  if (!feeHistory) {
    return null
  }

  const rewards = feeHistory.reward
    ?.map((r) => BigInt(r[0]))
    .filter((r) => r > 0)
    .sort()

  if (!rewards) {
    return null
  }

  const percentageIncreases = rewards.reduce<bigint[]>((acc, curr, i, arr) => {
    if (i !== arr.length - 1) {
      const next = arr[i + 1]
      const percentageIncrease = ((next - curr) / curr) * BigInt(PERCENT_MULTIPLIER)
      acc.push(percentageIncrease)
    }

    return acc
  }, [])

  const { highest, index } = findMax(percentageIncreases)

  const values =
    highest >= PRIORITY_FEE_INCREASE_BOUNDARY && index >= getMedian(rewards) ? rewards.slice(index) : rewards

  return values[getMedian(values)]
}

async function getPriorityFromChain(feeHistory: FeeHistory | undefined, rpcUrl: string) {
  try {
    const { data } = await axios.post<RpcCallParams, { data: MaxPriorityFeePerGasResponse }>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'eth_maxPriorityFeePerGas',
      params: [],
      id: 1,
    })

    return data.result
  } catch (err) {
    return calculatePriorityFeeEstimate(feeHistory)
  }
}

async function calculateFees({ baseFee, feeHistory, rpcUrl }: CalculateFeesParams) {
  const estimatedPriorityFee = await getPriorityFromChain(feeHistory, rpcUrl)

  const { highest: maxPriorityFeePerGas } = findMax([
    estimatedPriorityFee ? BigInt(estimatedPriorityFee) : etherNumbers.BG_ZERO,
    BigInt(MIN_PRIORITY),
  ])
  const maxFeePerGas = baseFee + maxPriorityFeePerGas

  return {
    baseFee,
    maxFeePerGas,
    maxPriorityFeePerGas,
  }
}
